// src/utils/officeCostRows.js
// Office costs for the cost calculations (Profitability, Project Cost Ledger,
// office realised profit, NL employer charges), read from both places they
// are kept:
//
//   office_overheads_yearly  typed or imported overhead lines, and the 2024 /
//                            2025 Rotterdam totals rebuilt from the ledger
//   expenses                 bank and card imports with is_office_expense set
//                            (Statement import writes them here since 2026-04)
//
// The result is a list in the overheads row shape, so every consumer keeps
// reading `year`, `category`, `officeLocation`, `amount` / `amountInInr`
// exactly as before.
//
// Overlap rule:
//   1. An office-year rebuilt from the ledger (any row whose import_source
//      starts with "grootboek-") reads the overheads table only. The ledger
//      already holds every bank and card payment of that year.
//   2. Anywhere else an imported expense wins: an overheads row is left out
//      when imported expenses exist for the same office, category and month
//      (from its expense_date). Rows with no date are always kept.
//
// Imported expenses count without BTW where the row has a split
// (net_amount, INR), otherwise at the bank amount.
//
// Rotterdam Salary Tax and Pension from the bank are full payments, while the
// 2026 salaries they are added to are gross (CAO matrix), so only the
// employer's share counts: NL_EMPLOYER_SHARE, taken from the 2025 payroll
// journals (loonjournaalposten). See CLAUDE.md, "NL Salary Tax and Pension".

export const LEDGER_IMPORT_PREFIX = 'grootboek-';

// 2025: employer charges 19,266.62 of 40,377.78 loonheffing (WHK, AWF, ZVW,
// AOF, WKO); employer part 8,434.52 of 15,247.21 PF Architectenbureaus.
export const NL_EMPLOYER_SHARE = { 'Salary Tax': 0.477, Pension: 0.553 };

const officeOf = (loc) => ((loc || 'NL') === 'IN' ? 'IN' : 'NL');
const monthOf = (date) => (date ? String(date).substring(0, 7) : null);

export function isImportedOfficeExpense(e) {
  return !!e && e.isOfficeExpense === true && !e.projectId && !!e.date;
}

export function buildOfficeCostRows(overheads = [], expenses = []) {
  const ledgerYears = new Set();
  overheads.forEach(o => {
    if (String(o.importSource || '').startsWith(LEDGER_IMPORT_PREFIX)) {
      ledgerYears.add(`${officeOf(o.officeLocation)}|${o.year}`);
    }
  });

  const expenseRows = [];
  const coveredMonths = new Set();
  expenses.forEach(e => {
    if (!isImportedOfficeExpense(e)) return;
    const office = officeOf(e.officeLocation);
    const ym = monthOf(e.date);
    const year = Number(ym.substring(0, 4));
    if (ledgerYears.has(`${office}|${year}`)) return;

    const category = e.category || 'Miscellaneous';
    const gross = e.amountInInr ?? e.amount ?? 0;
    const net = e.netAmount != null ? e.netAmount : gross;
    const share = office === 'NL' ? NL_EMPLOYER_SHARE[category] : undefined;
    const inr = share != null ? net * share : net;
    const quarter = Math.floor((Number(ym.substring(5, 7)) - 1) / 3) + 1;

    coveredMonths.add(`${office}|${ym}|${category}`);
    expenseRows.push({
      id: `exp-${e.id}`,
      year,
      quarter,
      category,
      subcategory: e.description || '',
      officeLocation: office,
      expenseDate: e.date,
      amount: inr,
      amountInInr: inr,
      employerShare: share ?? null,
      importSource: e.importSource || null,
      _fromExpenseId: e.id,
    });
  });

  const keptOverheads = overheads.filter(o => {
    const ym = monthOf(o.expenseDate);
    if (!ym) return true;
    return !coveredMonths.has(`${officeOf(o.officeLocation)}|${ym}|${o.category}`);
  });

  return [...keptOverheads, ...expenseRows];
}
